import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Modal,
  Notification,
  Paper,
  Popover,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconExternalLink, IconRefresh } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  downloadDeviceDayLog,
  getProduct,
  updateDevicePlayerPref,
} from "@utils/api-communication/RestApiCli";
import { useSelector } from "react-redux";
import { selectProductId } from "@utils/redux-utils/selectors/productSelectors";
import {
  ConfigKeyOption,
  LogModeOption,
  PLAYER_PREF_MIN_VERSION,
  PlayerPrefChannelKey,
  isDeviceOfflineError,
  normalizeConfigKey,
  normalizeLogMode,
  parseDeviceBool as parseBool,
  parseDeviceBoolWithDefault,
  pendingPlayerPrefValues,
  playerPrefDelivery,
  supportsHeadsetVersion,
  supportsPlayerPref,
} from "@utils/deviceConfigKeys";
import { FEATURE_VERSION_MAP } from "@constants/testConstants/versionConstants";
import { IProduct } from "types/productManagementTypes";
import DevicePlayerPrefsControls, {
  DevicePlayerPrefChange,
  confirmationCopy,
  playerPrefValue,
} from "@components/management/DevicePlayerPrefsControls";

interface Props {
  pid?: string;
}

type PendingChange = DevicePlayerPrefChange;

type BooleanPrefKind = Extract<PendingChange, { value: boolean }>["kind"];

const localDateISO = (): string => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const readDownloadError = async (error: any): Promise<string> => {
  const fallback = error?.message || "Could not download the log file.";
  const data = error?.response?.data;
  if (!(data instanceof Blob)) return fallback;

  const text = (await data.text()).trim();
  if (!text) return fallback;
  try {
    return String(JSON.parse(text)?.message ?? text);
  } catch {
    return text;
  }
};

const DeviceConfigurations: React.FC<Props> = ({ pid: pidProp }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pidOverride, setPidOverride] = useState<string | null>(null);
  const selectedProductId = useSelector(selectProductId);

  useEffect(() => {
    setPidOverride(null);
  }, [selectedProductId]);

  const pid = useMemo(
    () => pidOverride ?? pidProp ?? selectedProductId ?? "",
    [pidOverride, pidProp, selectedProductId],
  );

  const [pidPopoverOpened, setPidPopoverOpened] = useState(false);
  const [pidInput, setPidInput] = useState("");

  const openPidEditor = () => {
    setPidInput(pid);
    setPidPopoverOpened(true);
  };

  const applyPidInput = () => {
    const trimmed = pidInput.trim();
    if (!trimmed) return;
    setPidOverride(trimmed);
    setPidPopoverOpened(false);
  };

  const [product, setProduct] = useState<IProduct | null>(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [configKey, setConfigKey] = useState<ConfigKeyOption>("unset");
  const [enableLogger, setEnableLogger] = useState<boolean>(false);
  const [enableFileLogger, setEnableFileLogger] = useState<boolean>(true);
  const [enableLokiLogger, setEnableLokiLogger] = useState<boolean>(true);
  const [logMode, setLogMode] = useState<LogModeOption>("default");
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [disableKeyConfigOnTest, setDisableKeyConfigOnTest] = useState<boolean>(false);
  const [working, setWorking] = useState<PlayerPrefChannelKey | null>(null);
  const [logDate, setLogDate] = useState<string>(localDateISO);
  const [logDownloading, setLogDownloading] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    msg: string;
  } | null>(null);

  const notificationTimeoutRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (notificationTimeoutRef.current !== null) {
        window.clearTimeout(notificationTimeoutRef.current);
      }
    },
    [],
  );
  const showNotification = (type: "success" | "error", msg: string) => {
    if (notificationTimeoutRef.current !== null) {
      window.clearTimeout(notificationTimeoutRef.current);
    }
    setNotification({ type, msg });
    notificationTimeoutRef.current = window.setTimeout(() => {
      setNotification(null);
      notificationTimeoutRef.current = null;
    }, 3000);
  };

  const applyProductToState = useCallback((next: IProduct) => {
    const states = next?.states ?? {};
    const managementConfig = states.managementConfig ?? {};
    // Saved-but-unconfirmed values are what the admin chose, so they win.
    const playerPrefs = { ...(states.playerPrefs ?? {}), ...pendingPlayerPrefValues(next) };

    // Prefer playerPrefs (new schema); fall back to managementConfig.enableLogger for older headsets.
    const loggerValue =
      playerPrefs.enableLogger !== undefined
        ? parseBool(playerPrefs.enableLogger)
        : parseBool(managementConfig.enableLogger);
    setEnableLogger(loggerValue);
    setEnableFileLogger(parseDeviceBoolWithDefault(playerPrefs.enableFileLogger, true));
    setEnableLokiLogger(parseDeviceBoolWithDefault(playerPrefs.enableLokiLogger, true));
    setLogMode(normalizeLogMode(playerPrefs.logMode));
    setConfigKey(normalizeConfigKey(playerPrefs.configKey));
    setDisableKeyConfigOnTest(parseBool(playerPrefs.disableKeyConfigOnTest));
  }, []);

  const fetchProduct = useCallback(() => {
    if (!pid) return;
    setLoadingProduct(true);
    getProduct(
      pid,
      (resp) => {
        setLoadingProduct(false);
        const data = resp?.data ?? resp;
        if (!data) return;
        setProduct(data);
        applyProductToState(data);
      },
      () => {
        setLoadingProduct(false);
        showNotification("error", "Failed to load device state");
      },
    );
  }, [pid, applyProductToState]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const offlineMessage = (what: string) =>
    `Device is offline, so ${what} was not delivered. Send it again once the headset is connected.`;
  const queuedMessage = (what: string) =>
    `Device is offline. ${what} is saved and will be applied when the headset reconnects.`;

  const sendPref = (opts: {
    key: PlayerPrefChannelKey;
    value: string;
    what: string;
    successMsg: string;
    onSuccess?: () => void;
    onFailure?: () => void;
  }) => {
    if (!pid) {
      showNotification("error", "No device selected");
      return;
    }
    setWorking(opts.key);
    updateDevicePlayerPref(
      { pid, key: opts.key, value: opts.value },
      (response) => {
        setWorking(null);
        setPending(null);
        const delivery = playerPrefDelivery(response);
        if (delivery === "undelivered") {
          opts.onFailure?.();
          showNotification("error", offlineMessage(opts.what));
          return;
        }
        opts.onSuccess?.();
        if (delivery === "queued") {
          showNotification("success", queuedMessage(opts.what));
          fetchProduct();
          return;
        }
        showNotification("success", opts.successMsg);
      },
      (error) => {
        setWorking(null);
        setPending(null);
        opts.onFailure?.();
        showNotification(
          "error",
          isDeviceOfflineError(error)
            ? offlineMessage(opts.what)
            : `Failed to send ${opts.what} to the device`,
        );
      },
    );
  };

  const reportedConfigKey = () => normalizeConfigKey(product?.states?.playerPrefs?.configKey);

  const pendingWorking = working !== null;

  const requestChange = (change: PendingChange) => {
    if (!pid) {
      showNotification("error", "No device selected");
      return;
    }
    setPending(change);
  };

  const cancelPendingChange = () => {
    if (pendingWorking) return;
    setPending(null);
  };

  const booleanPrefSetters: Record<BooleanPrefKind, (value: boolean) => void> = {
    enableLogger: setEnableLogger,
    enableFileLogger: setEnableFileLogger,
    enableLokiLogger: setEnableLokiLogger,
    disableKeyConfigOnTest: setDisableKeyConfigOnTest,
  };

  const sendPendingChange = (change: PendingChange) => {
    const value = playerPrefValue(change);
    sendPref({
      key: change.kind,
      value,
      what: change.kind === "resetKeyConfig" ? "the key config reset" : change.kind,
      successMsg:
        change.kind === "resetKeyConfig"
          ? "Key config cleared on the device"
          : `${change.kind}: ${value || "unset"}`,
      onFailure: change.kind === "configKey" ? () => setConfigKey(reportedConfigKey()) : undefined,
      onSuccess: () => {
        if (change.kind === "logMode") setLogMode(change.value);
        else if (change.kind !== "configKey" && change.kind !== "resetKeyConfig") {
          booleanPrefSetters[change.kind](change.value);
        }
      },
    });
  };

  const confirmPendingChange = () => {
    if (!pending) return;
    if (!pid) {
      setPending(null);
      showNotification("error", "No device selected");
      return;
    }
    sendPendingChange(pending);
  };

  const supports = (key: PlayerPrefChannelKey) => supportsPlayerPref(key, product?.version);
  // Until the product is fetched there is no version to judge, so say nothing
  // rather than claim the device is too old.
  const versionNote = (label: string, minimum: string) =>
    product ? (
      <Text size="xs" c="dimmed">
        {t("DeviceConfig.versionRequired", {
          label,
          minimum,
          version: product.version || t("DeviceConfig.noVersion"),
        })}
      </Text>
    ) : null;
  const tooOldNote = (label: string, key: PlayerPrefChannelKey) =>
    versionNote(label, PLAYER_PREF_MIN_VERSION[key]);

  const dayLogSupported = supportsHeadsetVersion(
    FEATURE_VERSION_MAP.dayLogDownload,
    product?.version,
  );

  const handleDownloadLog = () => {
    if (!pid) {
      showNotification("error", "No device selected");
      return;
    }
    setLogDownloading(true);
    downloadDeviceDayLog(
      pid,
      logDate,
      (blob, filename) => {
        setLogDownloading(false);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        showNotification("success", `Saved ${filename}`);
      },
      (error) => {
        setLogDownloading(false);
        readDownloadError(error).then((msg) => showNotification("error", msg));
      },
    );
  };

  const states = product?.states ?? {};
  const stateRows: { label: string; value: string }[] = [
    { label: "Serial", value: states.serialNo ?? "-" },
    { label: "Model", value: states.model ?? "-" },
    { label: "PUI", value: states.pui ?? product?.puiVersion ?? "-" },
    { label: "App version", value: product?.version ?? "-" },
    { label: "Battery", value: product?.battery ?? "-" },
    { label: "Scene", value: product?.scene ?? "-" },
    {
      label: "Wi-Fi SSID",
      value: Array.isArray(states.wifi_ssid)
        ? states.wifi_ssid.join(", ")
        : (states.wifi_ssid ?? "-"),
    },
    { label: "Device IP", value: states.deviceIp ?? "-" },
    { label: "Brightness", value: states.brightness ?? "-" },
  ];

  return (
    <Container size="lg" px="md" pt="lg">
      <Group justify="space-between" align="center" mb="md">
        <Title order={2}>Device Configurations</Title>
        <Group gap="sm">
          <Popover
            opened={pidPopoverOpened}
            onChange={setPidPopoverOpened}
            position="bottom-end"
            withArrow
            trapFocus
            shadow="md"
          >
            <Popover.Target>
              <UnstyledButton onClick={openPidEditor} aria-label="Change PID">
                {pid ? (
                  <Badge variant="light" color="blue" size="lg" style={{ cursor: "pointer" }}>
                    {pid}
                  </Badge>
                ) : (
                  <Badge variant="light" color="gray" size="lg" style={{ cursor: "pointer" }}>
                    No device selected
                  </Badge>
                )}
              </UnstyledButton>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  Enter a device PID
                </Text>
                <Group gap="xs" wrap="nowrap">
                  <TextInput
                    value={pidInput}
                    onChange={(e) => setPidInput(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") applyPidInput();
                    }}
                    placeholder="PID"
                    size="xs"
                    autoFocus
                    data-autofocus
                    w={220}
                  />
                  <Button size="xs" onClick={applyPidInput} disabled={!pidInput.trim()}>
                    Apply
                  </Button>
                </Group>
              </Stack>
            </Popover.Dropdown>
          </Popover>
          <Button
            variant="light"
            size="sm"
            rightSection={<IconExternalLink size={16} />}
            onClick={() => navigate(`/product-status?pid=${encodeURIComponent(pid)}`)}
            disabled={!pid}
            data-testid="device-config-product-status-link"
          >
            Product Status
          </Button>
          <Button
            variant="default"
            size="sm"
            leftSection={<IconRefresh size={16} />}
            onClick={fetchProduct}
            disabled={!pid || loadingProduct}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {notification && (
        <Notification
          color={notification.type === "success" ? "green" : "red"}
          onClose={() => setNotification(null)}
          withCloseButton
          mb="md"
        >
          {notification.msg}
        </Notification>
      )}

      <Grid gutter="md" align="stretch">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper shadow="xs" p="md" withBorder h="100%">
            {!pid ? (
              <Text size="sm" c="dimmed">
                Select a device from the device list to manage its online configuration.
              </Text>
            ) : (
              <>
                <Group justify="space-between" mb="sm">
                  <Title order={5}>Current device state</Title>
                  {loadingProduct && <Loader size="xs" />}
                </Group>
                <Card withBorder radius="sm" padding={0}>
                  <Table striped verticalSpacing="xs" horizontalSpacing="sm">
                    <Table.Tbody>
                      {stateRows.map((row) => (
                        <Table.Tr key={row.label}>
                          <Table.Td>
                            <Text size="xs" c="dimmed">
                              {row.label}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs" style={{ wordBreak: "break-word" }}>
                              {row.value || "-"}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Card>
              </>
            )}
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper shadow="xs" p="md" withBorder h="100%">
            <Stack gap="md">
              <DevicePlayerPrefsControls
                configKey={configKey}
                enableLogger={enableLogger}
                enableFileLogger={enableFileLogger}
                enableLokiLogger={enableLokiLogger}
                logMode={logMode}
                disableKeyConfigOnTest={disableKeyConfigOnTest}
                pendingChange={pending}
                queued={pendingPlayerPrefValues(product)}
                working={working}
                onConfigKeyChange={setConfigKey}
                onRequestChange={requestChange}
                supports={supports}
                tooOldNote={tooOldNote}
                showDividers={!!product}
                expectedSerial={product?.states?.serialNo}
              />

              {product && <Divider />}

              {!dayLogSupported ? (
                versionNote(t("DeviceConfig.dayLogTitle"), FEATURE_VERSION_MAP.dayLogDownload)
              ) : (
                <Stack gap="xs">
                  <Stack gap={4}>
                    <Title order={5}>{t("DeviceConfig.dayLogTitle")}</Title>
                    <Text size="xs" c="dimmed">
                      {t("DeviceConfig.dayLogDescription")}
                    </Text>
                  </Stack>
                  <Group align="flex-end" gap="sm" wrap="nowrap">
                    <TextInput
                      type="date"
                      label={t("DeviceConfig.dayLogDate")}
                      aria-label={t("DeviceConfig.dayLogDate")}
                      value={logDate}
                      max={localDateISO()}
                      onChange={(e) => setLogDate(e.currentTarget.value)}
                      disabled={logDownloading}
                      w={190}
                    />
                    <Button
                      onClick={handleDownloadLog}
                      loading={logDownloading}
                      disabled={!logDate}
                    >
                      {t("DeviceConfig.dayLogDownload")}
                    </Button>
                  </Group>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      <Modal
        opened={pending !== null}
        onClose={cancelPendingChange}
        title={pending ? confirmationCopy(pending, t).title : ""}
        centered
        closeOnClickOutside={!pendingWorking}
        closeOnEscape={!pendingWorking}
        withCloseButton={!pendingWorking}
      >
        {pending && (
          <Stack gap="md">
            <Text size="sm">{confirmationCopy(pending, t).message}</Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={cancelPendingChange} disabled={pendingWorking}>
                {t("cancel")}
              </Button>
              <Button
                color={confirmationCopy(pending, t).color}
                onClick={confirmPendingChange}
                loading={pendingWorking}
              >
                {t("confirm")}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
};

export default DeviceConfigurations;
